<template>
  <div class="password-reset-page">
    <div class="password-reset-container">
      <h2>{{ resetMode === 'forced' ? 'You are required to set a new password' : 'Set New Password' }}</h2>
      <form @submit.prevent="resetPasswordHandler">
        <div class="form-group">
          <label for="new-password">New Password:</label>
          <input
              type="password"
              id="new-password"
              v-model="newPassword"
              required
              :disabled="loading"
          />
        </div>
        <div class="form-group">
          <label for="confirm-password">Confirm New Password:</label>
          <input
              type="password"
              id="confirm-password"
              v-model="confirmPassword"
              required
              :disabled="loading"
          />
        </div>
        <button type="submit" :disabled="loading" class="reset-button">
          <span
              v-if="loading"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
          ></span>
          {{ loading ? 'Resetting...' : resetButtonText }}
        </button>
      </form>
      <p v-if="error" class="error-message">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {getCurrentUser} from "aws-amplify/auth";

export default {
  props: {
    resetMode: {
      type: String,
      required: true,
      validator: (value) => ['forced', 'magic'].includes(value),
    },
  },
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['getErrorMessage', 'isLoading']),
    error() {
      return this.getErrorMessage;
    },
    loading() {
      return this.isLoading;
    },
    resetButtonText() {
      return this.resetMode === 'forced' ? 'Set new password' : 'Reset password';
    },
  },
  methods: {
    ...mapActions('auth', ['resetPassword', 'fetchSession']),
    async resetPasswordHandler() {
      if (this.newPassword !== this.confirmPassword) {
        this.$store.commit('auth/SET_ERROR', 'New password and confirm password do not match');
        return;
      }

      try {
        const result = await this.resetPassword({
          newPassword: this.newPassword,
          resetMode: this.resetMode,
          magicLink: this.$route.query.magicLink || null,
        });
        if (result) {
          console.log('Password reset successful - redirecting to profile');
          //fetch user session
          const user = await getCurrentUser();
          await this.fetchSession(user);
          this.$router.push('/profile');
        }
      } catch (error) {
        console.error('Password reset error:', error);
      }
    },
  },
};
</script>

<style scoped>
.password-reset-page {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: clamp(24px, 6vw, 64px) 20px;
  min-height: 60vh;
}
.password-reset-container {
  max-width: 420px;
  width: 100%;
  padding: 32px;
  border-radius: var(--radius);
  background: var(--card);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
}
.password-reset-container h2 { font-size: 24px; font-weight: 400; letter-spacing: -.01em; margin: 0 0 20px; }

.form-group {
  position: relative;
  margin-bottom: 18px;
}

label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
}

input {
  width: 100%;
  padding: 11px 13px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  background: var(--surface);
  color: var(--text);
  font-size: 14px;
}
input:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring); }

button {
  display: block;
  width: 100%;
  padding: 12px;
  background: var(--accent);
  color: var(--on-accent);
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}
button:hover:not(:disabled) { background: var(--accent-h); }
button:disabled { opacity: .6; cursor: not-allowed; }

.error-message {
  color: var(--danger);
  margin-top: 10px;
  font-size: 13px;
}
</style>